import React, { useState, useEffect } from "react";
import axios from "axios";
import "./UserManagementPage.css";

// Modal Component for Create and Edit Forms
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>
          X
        </button>
        {children}
      </div>
    </div>
  );
};

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: "",
    name: "",
    email: "",
    password: "",
    contact: "",
    role: "Manager", // Default role
    active: true,
    userPhoto: null, // Add photo field
  });
  const [editUser, setEditUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeactivationErrorModalOpen, setIsDeactivationErrorModalOpen] = useState(false);

  const getApiUrl = () => {
    return "/api"; // Standardized API base path
  };

  const API_URL = getApiUrl(); // Use standardized API URL

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleApiError = (err) => {
    if (err.response && err.response.status === 401) {
      localStorage.removeItem("authToken");
      window.location.reload(); // or redirect to login page if you have routing
      return;
    }
    // Optionally handle other errors
  };

  const fetchUsers = () => {
    setLoading(true);
    const token = localStorage.getItem("authToken");

    axios
      .get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setUsers(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        handleApiError(err);
      });
  };

  const uploadUserPhotoAndGetFilename = async (file) => {
    const token = localStorage.getItem("authToken");
    const formData = new FormData();
    formData.append("user_photo", file);

    const response = await axios.post(`${API_URL}/users/upload-photo`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.user_photo;
  };

  const handleCreateUser = async () => {
    let userPhotoFilename = "";
    if (newUser.userPhoto instanceof File) {
      try {
        userPhotoFilename = await uploadUserPhotoAndGetFilename(newUser.userPhoto);
      } catch (err) {
        handleApiError(err);
        console.error("Photo upload error:", err);
        return;
      }
    }

    const userData = {
      username: newUser.username,
      name: newUser.name,
      email: newUser.email,
      password: newUser.password,
      contact: newUser.contact,
      role: newUser.role,
      active: newUser.active,
      user_photo: userPhotoFilename || "",
    };

    const token = localStorage.getItem("authToken");

    try {
      await axios.post(`${API_URL}/users`, userData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      fetchUsers();
      setNewUser({
        username: "",
        name: "",
        email: "",
        password: "",
        contact: "",
        role: "Manager",
        active: true,
        userPhoto: null,
      });
      setIsModalOpen(false);
    } catch (err) {
      handleApiError(err);
      console.error("Create user error:", err);
    }
  };

  // Unified function for both opening the edit modal and updating the user state from modal inputs
  const getUserEditState = (user) => ({
    id: user.id,
    username: user.username || "",
    name: user.name || "",
    email: user.email || "",
    password: "", // never prefill password
    contact: user.contact || "",
    role: user.role || "Manager",
    active: user.active,
    userPhoto: user.user_photo || null,
  });

  // Use this for opening the modal for editing
  const handleEditUser = (user) => {
    setEditUser(getUserEditState(user));
    setIsModalOpen(true);
  };

  const handleUpdateUser = async () => {
    const token = localStorage.getItem("authToken");
    let userPhotoFilename = editUser.userPhoto;

    // Only upload if a new file is selected
    if (editUser.userPhoto instanceof File) {
      try {
        userPhotoFilename = await uploadUserPhotoAndGetFilename(editUser.userPhoto);
      } catch (err) {
        handleApiError(err);
        console.error("Photo upload error:", err);
        return;
      }
    }

    const userData = {
      username: editUser.username,
      name: editUser.name,
      email: editUser.email,
      password: editUser.password,
      contact: editUser.contact,
      role: editUser.role,
      active: editUser.active,
      user_photo: typeof userPhotoFilename === "string" ? userPhotoFilename : "",
    };

    try {
      await axios.put(`${API_URL}/users/${editUser.id}`, userData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      fetchUsers();
      setEditUser(null);
      setIsModalOpen(false);
    } catch (err) {
      handleApiError(err);
      console.error("Update user error:", err);
    }
  };

  const handleDeleteUser = (userId) => {
    const token = localStorage.getItem("authToken");

    axios
      .delete(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => fetchUsers())
      .catch((err) => {
        handleApiError(err);
        console.error("Delete user error:", err);
      });
  };

  const handleToggleUserStatus = (userId, active) => {
    const activeUsersCount = users.filter((user) => user.active).length;

    if (active && activeUsersCount <= 1) {
      setIsDeactivationErrorModalOpen(true);
      return;
    }

    const token = localStorage.getItem("authToken");

    axios
      .put(
        `${API_URL}/users/${userId}/status`,
        { active: !active },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => fetchUsers())
      .catch((err) => {
        handleApiError(err);
        console.error("Toggle user status error:", err);
      });
  };

  // --- In the Modal, ensure you NEVER call setEditUser except in onChange handlers and handleEditUser ---

  return (
    <div className="user-management">
      <h1>Gestão de Utilizadores</h1>

      {/* Create User Button */}
      <button
        className="create-user-button"
        onClick={() => {
          setNewUser({
            username: "",
            name: "",
            email: "",
            password: "",
            contact: "",
            role: "Manager",
            active: true,
            userPhoto: null,
          });
          setEditUser(null);
          setIsModalOpen(true);
        }}
      >
        Criar novo utilizador
      </button>

      {/* Create/Edit User Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <h2>{editUser ? "Editar Utilizador" : "Criar Utilizador"}</h2>
        <input
          type="text"
          placeholder="Nome de Utilizador"
          value={editUser ? editUser.username : newUser.username}
          onChange={(e) =>
            editUser
              ? setEditUser({ ...editUser, username: e.target.value })
              : setNewUser({ ...newUser, username: e.target.value })
          }
        />
        <input
          type="text"
          placeholder="Nome"
          value={editUser ? editUser.name : newUser.name}
          onChange={(e) =>
            editUser
              ? setEditUser({ ...editUser, name: e.target.value })
              : setNewUser({ ...newUser, name: e.target.value })
          }
        />
        <input
          type="email"
          placeholder="Email"
          value={editUser ? editUser.email : newUser.email}
          onChange={(e) =>
            editUser
              ? setEditUser({ ...editUser, email: e.target.value })
              : setNewUser({ ...newUser, email: e.target.value })
          }
        />
        <input
          type="password"
          placeholder="Palavra-passe"
          value={editUser ? editUser.password : newUser.password}
          onChange={(e) =>
            editUser
              ? setEditUser({ ...editUser, password: e.target.value })
              : setNewUser({ ...newUser, password: e.target.value })
          }
        />
        <input
          type="text"
          placeholder="Contacto"
          value={editUser ? editUser.contact : newUser.contact}
          onChange={(e) =>
            editUser
              ? setEditUser({ ...editUser, contact: e.target.value })
              : setNewUser({ ...newUser, contact: e.target.value })
          }
        />
        <select
          value={editUser ? editUser.role : newUser.role}
          onChange={(e) =>
            editUser
              ? setEditUser({ ...editUser, role: e.target.value })
              : setNewUser({ ...newUser, role: e.target.value })
          }
        >
          <option value="Admin">Admin</option>
          <option value="Manager">Gestor</option>
        </select>
        <input
          type="file"
          accept="image/*"
          onChange={(e) =>
            editUser
              ? setEditUser({ ...editUser, userPhoto: e.target.files[0] })
              : setNewUser({ ...newUser, userPhoto: e.target.files[0] })
          }
        />
        <button onClick={editUser ? handleUpdateUser : handleCreateUser}>
          {editUser ? "Atualizar Utilizador" : "Criar Utilizador"}
        </button>
      </Modal>

      {/* Deactivation Error Modal */}
      <Modal
        isOpen={isDeactivationErrorModalOpen}
        onClose={() => setIsDeactivationErrorModalOpen(false)}
      >
        <h2>Erro</h2>
        <p>Não pode desativar o último utilizador ativo.</p>
        <button onClick={() => setIsDeactivationErrorModalOpen(false)}>
          Fechar
        </button>
      </Modal>

      {/* User List */}
      <div className="user-list-container">
        {loading ? (
          <p>Carregar Utilizadores...</p>
        ) : (
          <ul className="user-list">
            {users.map((user) => (
              <li key={user.id} className="user-item">
                <button
                  onClick={() => handleToggleUserStatus(user.id, user.active)}
                  className={`status-button ${user.active ? "active" : "inactive"}`}
                >
                  {user.active ? "Ativo" : "Inativo"}
                </button>
                <span>
                  {user.username} ({user.name}) - {user.role}
                </span>
                <div className="user-actions">
                  <button
                    onClick={() => handleEditUser(user)}
                  >
                    Editar
                  </button>
                  <button onClick={() => handleDeleteUser(user.id)}>
                    Apagar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;
